import { initRegistry, loadBuiltinPlugins, topologyRegistry } from "../plugins";
import type { TopologyInitResult } from "../plugins/types";
import { getRelationProfile, packPlanar } from "./relationProfile";
import type { SimParams } from "./params";
import type { Rng } from "./rng";

// Idempotent — safe from tests and Simulation without going through main.ts
loadBuiltinPlugins();

export type TopologyState = {
  targets: Int32Array;
  friends: Int32Array;
  enemies: Int32Array;
  types: Uint8Array | null;
  roleCount: number;
};

export function initPositions(params: SimParams, rng: Rng): Float64Array {
  const out = new Float64Array(params.n * params.dim);
  const plugin =
    initRegistry.tryGet(params.initPositions) ??
    initRegistry.get("uniform_box");
  plugin.init(params, rng, out);
  return out;
}

/** Normalize topology plugin result into planar targets + optional types. */
export function normalizeTopologyResult(
  params: SimParams,
  result: TopologyInitResult,
): TopologyState {
  const n = params.n;
  const profile = getRelationProfile(params.relationProfile);
  const R = profile.roles.length;

  let targets: Int32Array;
  if (result.targets) {
    if (result.targets.length !== n * R) {
      throw new Error(
        `topology targets length ${result.targets.length} !== n*R (${n}*${R})`,
      );
    }
    targets = result.targets;
  } else if (result.friends && result.enemies) {
    if (R !== 2) {
      throw new Error(
        "legacy friends/enemies topology requires classic_fe R=2",
      );
    }
    if (result.friends.length !== n || result.enemies.length !== n) {
      throw new Error("friends/enemies length must equal n");
    }
    targets = packPlanar(result.friends, result.enemies);
  } else {
    throw new Error("topology must return targets or friends+enemies");
  }

  let types: Uint8Array | null = result.types ?? null;
  if (types) {
    if (types.length !== n) throw new Error("types length must equal n");
  } else if (params.typeCount > 1) {
    throw new Error(
      `topology ${params.topology} must supply types when typeCount=${params.typeCount}`,
    );
  }

  const friends = targets.subarray(0, n);
  const enemies = R >= 2 ? targets.subarray(n, 2 * n) : targets.subarray(0, 0);

  return { targets, friends, enemies, types, roleCount: R };
}

export function initTopology(params: SimParams, rng: Rng): TopologyState {
  const plugin =
    topologyRegistry.tryGet(params.topology) ?? topologyRegistry.get("random");
  const raw = plugin.init(params, rng);
  return normalizeTopologyResult(params, raw);
}
